export type FitbitExtractionUiStatus = 'running' | 'completed' | 'failed';

export interface FitbitExtractionStatusSemantics {
  uiStatus: FitbitExtractionUiStatus;
  isTerminal: boolean;
  isSuccessful: boolean;
}

export interface FitbitExtractionProgressPresentation {
  phase: 'starting' | 'checking' | 'waiting' | 'queued' | 'writing' | 'uploading' | 'completed' | 'terminal' | 'legacy' | 'unknown';
  variant: 'determinate' | 'indeterminate';
  value: number | null;
  valueLabel: string | null;
  label: string;
}

export interface FitbitExtractionProgressInput {
  backendStatus?: unknown;
  backendPercentage?: unknown;
  processedDates?: unknown;
  totalDates?: unknown;
  progressMessage?: unknown;
}

const SUCCESSFUL_TERMINAL_STATUSES = new Set([
  'completed',
  'completed_with_errors',
]);

const FAILED_TERMINAL_STATUSES = new Set([
  'failed',
  'rate_limited',
  'cancelled',
]);

/**
 * Normalizes backend Fitbit extraction states for the research dashboard.
 * Unknown and legacy in-progress states remain non-terminal so a future backend
 * state cannot accidentally be shown as a successful extraction.
 */
export function getFitbitExtractionStatusSemantics(
  backendStatus: unknown
): FitbitExtractionStatusSemantics {
  const status = typeof backendStatus === 'string' ? backendStatus : '';

  if (SUCCESSFUL_TERMINAL_STATUSES.has(status)) {
    return {
      uiStatus: 'completed',
      isTerminal: true,
      isSuccessful: true,
    };
  }

  if (FAILED_TERMINAL_STATUSES.has(status)) {
    return {
      uiStatus: 'failed',
      isTerminal: true,
      isSuccessful: false,
    };
  }

  return {
    uiStatus: 'running',
    isTerminal: false,
    isSuccessful: false,
  };
}

function finiteNumber(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampPercent(value: unknown): number {
  return Math.min(100, Math.max(0, Math.round(finiteNumber(value) ?? 0)));
}

function unitPercent(processedDates: unknown, totalDates: unknown): number | null {
  const processed = finiteNumber(processedDates);
  const total = finiteNumber(totalDates);
  if (processed === null || total === null || processed < 0 || total <= 0) return null;
  return clampPercent((Math.min(processed, total) / total) * 100);
}

function scaleWorkloadProgress(phasePercent: number | null, start: number, end: number): number {
  if (phasePercent === null) return start;
  return clampPercent(start + ((end - start) * phasePercent) / 100);
}

/**
 * Presents progress using the unit that is actually measurable in each phase.
 * The backend's legacy percentage is stage-weighted, so data_available=90 does
 * not mean that 90% of the extraction has been performed.
 */
export function getFitbitExtractionProgressPresentation({
  backendStatus,
  backendPercentage,
  processedDates,
  totalDates,
  progressMessage,
}: FitbitExtractionProgressInput): FitbitExtractionProgressPresentation {
  const status = typeof backendStatus === 'string' ? backendStatus : '';
  const message = typeof progressMessage === 'string' ? progressMessage.trim() : '';
  const measuredPercent = unitPercent(processedDates, totalDates);
  const legacyPercent = clampPercent(backendPercentage);

  if (status === 'completed' || status === 'completed_with_errors') {
    return { phase: 'completed', variant: 'determinate', value: 100, valueLabel: '100%', label: 'Complete' };
  }

  if (status === 'failed' || status === 'rate_limited' || status === 'cancelled') {
    const fallback = status === 'cancelled' ? 'Cancelled' : status === 'rate_limited' ? 'Rate limited' : 'Failed';
    return { phase: 'terminal', variant: 'indeterminate', value: null, valueLabel: null, label: fallback };
  }

  if (status === 'data_available') {
    return { phase: 'queued', variant: 'determinate', value: 45, valueLabel: '45%', label: 'Waiting for worker' };
  }

  if (status === 'preparing_results' || status === 'uploading') {
    const uploading = status === 'uploading' || /upload/i.test(message) || legacyPercent >= 99;
    if (uploading) {
      return { phase: 'uploading', variant: 'determinate', value: 98, valueLabel: '98%', label: 'Uploading file' };
    }
    const estimated = scaleWorkloadProgress(measuredPercent, 45, 95);
    return { phase: 'writing', variant: 'determinate', value: estimated, valueLabel: `${estimated}%`, label: 'Building file' };
  }

  if (status === 'checking_database') {
    const estimated = scaleWorkloadProgress(measuredPercent, 2, 10);
    return {
      phase: 'checking',
      variant: 'determinate',
      value: estimated,
      valueLabel: `${estimated}%`,
      label: 'Checking data',
    };
  }

  if (['waiting_for_data', 'fetching_missing_data', 'backfilling', 'storing_data', 'running'].includes(status)) {
    const estimated = scaleWorkloadProgress(measuredPercent, 10, 45);
    return {
      phase: 'waiting',
      variant: 'determinate',
      value: estimated,
      valueLabel: `${estimated}%`,
      label: 'Preparing data',
    };
  }

  if (status === 'starting') {
    return { phase: 'starting', variant: 'determinate', value: 2, valueLabel: '2%', label: 'Starting' };
  }

  // Preserve existing HealthKit/local-job behavior until those endpoints expose
  // the same raw phase information.
  if (!status) {
    return { phase: 'legacy', variant: 'determinate', value: legacyPercent, valueLabel: `${legacyPercent}%`, label: 'Processing' };
  }

  return { phase: 'unknown', variant: 'indeterminate', value: null, valueLabel: null, label: 'Processing' };
}

export interface FitbitExtractionDeleteResponse {
  ok: boolean;
  status: number;
  success?: boolean;
}

/**
 * A 404 means the authoritative backend job is already gone, so its stale local
 * copy can be removed. Other failures must remain visible for a later retry.
 */
export function shouldRemoveFitbitExtractionJobLocally(
  response: FitbitExtractionDeleteResponse
): boolean {
  if (response.status === 404) {
    return true;
  }

  return response.ok && response.success !== false;
}

export interface FitbitExtractionRegistrationRecord {
  jobId?: unknown;
  clientRequestId?: unknown;
}

export interface WaitForFitbitExtractionJobOptions {
  clientRequestId: string;
  loadJobs: () => Promise<FitbitExtractionRegistrationRecord[]>;
  maxAttempts?: number;
  retryDelayMs?: number;
  wait?: (delayMs: number) => Promise<void>;
}

/**
 * Resolves the authoritative Firestore job created by a long-running extraction
 * request. Exact request correlation avoids targeting an older extraction that
 * happens to have the same participant, name, and date range.
 */
export async function waitForFitbitExtractionJobId({
  clientRequestId,
  loadJobs,
  maxAttempts = 20,
  retryDelayMs = 250,
  wait = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs)),
}: WaitForFitbitExtractionJobOptions): Promise<string | null> {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const jobs = await loadJobs();
    const match = jobs.find((job) =>
      job.clientRequestId === clientRequestId &&
      typeof job.jobId === 'string' &&
      job.jobId.length > 0
    );

    if (match && typeof match.jobId === 'string') {
      return match.jobId;
    }

    if (attempt < maxAttempts) {
      await wait(retryDelayMs);
    }
  }

  return null;
}
